import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { register, registerOwner } from "@/lib/api"; // ← AGREGAR registerOwner
import { toast } from "@/hooks/use-toast";
import { Loader2, Store, Building, User } from "lucide-react";
import {
  setCurrentShop,
  setCurrentUser,
  setCurrentOwner,
  setOwnerShops,
  setActiveShopId,
} from "@/lib/auth";
import type { Shop } from "@shared/schema";
import { Link } from "wouter";

export default function RegisterShop() {
  const [, setLocation] = useLocation();

  // Leer parámetros de la URL
  const [location] = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const planFromUrl = searchParams.get("plan");

  // Estado para el plan seleccionado
  const [selectedPlan, setSelectedPlan] = useState<
    "basic" | "professional" | "enterprise"
  >(
    planFromUrl === "professional"
      ? "professional"
      : planFromUrl === "enterprise"
        ? "enterprise"
        : "basic",
  );

  // Guardar el plan seleccionado en localStorage para análisis
  useEffect(() => {
    if (planFromUrl) {
      localStorage.setItem("selected_plan", planFromUrl);
      console.log("📊 Plan seleccionado desde URL guardado:", planFromUrl);
    }
  }, [planFromUrl]);

  // También guardar cuando el usuario cambia el plan manualmente
  useEffect(() => {
    localStorage.setItem("current_selected_plan", selectedPlan);
  }, [selectedPlan]);

  // Formulario para registro unificado
  const [formData, setFormData] = useState({
    businessName: "",
    ownerName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // Mutación para registro unificado con plan
  const registerMutation = useMutation({
    mutationFn: () =>
      registerOwner({
        businessName: formData.businessName,
        ownerName: formData.ownerName,
        email: formData.email,
        phone: formData.phone || "",
        password: formData.password,
        planType: selectedPlan,
      }),
    onSuccess: (data: {
      owner: any;
      message?: string;
      success?: boolean;
      verificationToken?: string;
    }) => {
      // ============ MOSTRAR ENLACE DE VERIFICACIÓN EN DESARROLLO ============
      const isDev =
        import.meta.env.DEV || window.location.hostname === "localhost";

      if (isDev && data.verificationToken) {
        // Construir la URL completa de verificación
        const baseUrl = window.location.origin;
        const verificationUrl = `${baseUrl}/api/auth/verify-email?token=${data.verificationToken}`;

        // Mostrar un toast especial con el enlace
        toast({
          title: "🔧 MODO DESARROLLO - Enlace de verificación",
          description: (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm font-mono break-all mb-2">
                {verificationUrl}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-yellow-100 hover:bg-yellow-200 border-yellow-300"
                onClick={() => {
                  navigator.clipboard.writeText(verificationUrl);
                  toast({
                    title: "✅ Enlace copiado",
                    description:
                      "El enlace de verificación ha sido copiado al portapapeles",
                  });
                }}
              >
                📋 Copiar enlace
              </Button>
              <p className="text-xs text-yellow-700 mt-2">
                ⚠️ Esto solo se muestra en desarrollo. Haz clic en el enlace o
                cópialo en tu navegador para verificar la cuenta.
              </p>
            </div>
          ),
          duration: 30000, // 30 segundos para que alcances a copiarlo
        });

        // También mostrarlo en consola para fácil acceso
        console.log("🔗 ===== ENLACE DE VERIFICACIÓN (copia y pega) =====");
        console.log(verificationUrl);
        console.log("==================================================");
      } else {
        // Mensaje normal para producción
        toast({
          title: "📧 Revisa tu email",
          description: `Hemos enviado un enlace de verificación a ${formData.email}. Por favor, revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.`,
          duration: 10000,
        });
      }

      // Guardar email en localStorage para posible reenvío
      localStorage.setItem("pendingVerificationEmail", formData.email);

      // Redirigir a una página de "verifica tu email"
      setLocation("/verify-email-pending");
    },
    onError: (error: Error) => {
      // ============ Manejar error específico de email ya registrado ============
      if (error.message.includes("ya está registrado")) {
        toast({
          title: "Email ya registrado",
          description:
            "Este email ya está registrado. Por favor, inicia sesión o verifica tu email.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    },
  });

  const isPending = registerMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    // ============ VALIDACIÓN DE TELÉFONO CUBANO ============
    const phoneRegex = /^[56][0-9]{7}$/;
    const cleanPhone = formData.phone.replace(/\s/g, "");

    // Verificar si tiene formato cubano (8 dígitos empezando con 5 o 6)
    if (!phoneRegex.test(cleanPhone)) {
      // Permitir formato con +53
      const withCountryCode = /^\+53[56][0-9]{7}$/;
      if (!withCountryCode.test(cleanPhone)) {
        toast({
          title: "Error",
          description:
            "Número de teléfono inválido. Debe ser un móvil cubano (ej: 51234567, 61234567 o +5351234567)",
          variant: "destructive",
        });
        return;
      }
    }
    // ============ FIN VALIDACIÓN ============

    registerMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md shadow-xl border-primary/10">
        <CardHeader className="text-center border-b bg-muted/10 pb-8">
          <div className="flex justify-center mb-4">
            <Building className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading font-bold uppercase tracking-wider">
            Crear Nueva Cuenta
          </CardTitle>

          <p className="text-sm text-muted-foreground mt-2">
            Registra tu negocio para gestionar tus puntos de venta
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {/* Formulario de registro unificado */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="businessName">Nombre de la Empresa *</Label>
              <Input
                id="businessName"
                type="text"
                placeholder="Ej: Supermercados García"
                value={formData.businessName}
                onChange={(e) =>
                  setFormData({ ...formData, businessName: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ownerName">Nombre del Propietario *</Label>
              <Input
                id="ownerName"
                type="text"
                placeholder="Ej: Carlos García"
                value={formData.ownerName}
                onChange={(e) =>
                  setFormData({ ...formData, ownerName: e.target.value })
                }
                required
              />
            </div>

            {/* Selector de Plan */}
            <div className="mb-6">
              <Label htmlFor="plan-select" className="mb-2 block">
                Selecciona tu plan *
              </Label>
              <select
                id="plan-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={selectedPlan}
                onChange={(e) =>
                  setSelectedPlan(
                    e.target.value as "basic" | "professional" | "enterprise",
                  )
                }
              >
                <option value="basic">Básico - Gratis</option>
                <option value="professional">Profesional - $29.99/mes</option>
                <option value="enterprise">Enterprise - $99.99/mes</option>
              </select>
              {planFromUrl && (
                <p className="text-xs text-green-600 mt-2">
                  ✅ Has seleccionado el plan{" "}
                  {planFromUrl === "basic"
                    ? "Básico"
                    : planFromUrl === "professional"
                      ? "Profesional"
                      : "Enterprise"}{" "}
                  desde la página de precios
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico *</Label>
              <Input
                id="email"
                type="email"
                placeholder="Ej: empresa@ejemplo.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono Móvil *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Ej: 51234567, 61234567 o +53 51234567"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                required
              />
              <p className="text-xs text-muted-foreground">
                Teléfonos móviles cubanos (empieza con 5 o 6, 8 dígitos)
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Contraseña *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repite la contraseña"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Información importante:</strong> Al registrarte, se
                creará automáticamente tu primera tienda llamada "
                <strong>
                  {formData.businessName || "Tu Empresa"} - Sucursal Principal
                </strong>
                ".
                {formData.businessName && (
                  <span className="block mt-2">
                    Podrás crear más tiendas según tu plan de suscripción.
                  </span>
                )}
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                "Crear Mi Cuenta"
              )}
            </Button>
          </form>

          <div className="text-center pt-4 border-t mt-6">
            <p className="text-sm text-muted-foreground">
              ¿Ya tienes una cuenta?{" "}
              <Link
                href="/shop/login"
                className="text-primary hover:underline font-medium"
              >
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
